import { AxiosError, AxiosInstance } from 'axios';
import { toast } from 'react-hot-toast';
import { appSession } from '@/shared/lib';
import { getFieldErrorMessage } from '../error-handler';
import { refreshAccessToken } from '../refresh-access-token';
import { RequestError } from '../types';
import { addAuthHeader } from './request-interceptors';

function responseErrorHandlerBuilder(instance: AxiosInstance) {
  return async (err: any) => {
    const originalRequest = err.config;

    if (
      err.response?.data.errors?.[0]?.msg.includes('Signature has expired') ||
      (err.response?.data.python_exc?.includes('Signature has expired') &&
        !originalRequest._retry)
    ) {
      originalRequest._retry = true;
      const response = await refreshAccessToken();
      const result = response.data.result;
      instance.defaults.headers.common['Authorization'] =
        'Bearer ' + result.access_token;

      return instance(originalRequest);
    }

    if (err.response?.data.errors?.[0]?.msg.includes('Wrong refresh token')) {
      appSession.removeItem();
      window.location.reload();
    }

    if (err instanceof AxiosError) {
      if (err.response?.status === 403) {
        toast.error('403 Access denied: ' + originalRequest.url);
        return Promise.reject(err);
      }

      if (err.response?.status === 401) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const response = await refreshAccessToken();
            const result = response.data.result;
            instance.defaults.headers.common['Authorization'] =
              'Bearer ' + result.access_token;

            return instance(originalRequest);
          } catch (err) {
            appSession.removeItem();
            window.location.reload();
          }
        } else {
          appSession.removeItem();
          window.location.reload();
        }
      }

      const error = err as AxiosError<RequestError>;

      if (
        error.response?.data.type === 'field' &&
        error.response?.data.service === 'gateway'
      ) {
        toast.error(getFieldErrorMessage(error)!);
      } else if (
        error.response?.data.service === 'gateway.auth' ||
        error.response?.data.type !== 'field'
      ) {
        toast.error(getFieldErrorMessage(error)!);
      } else {
        toast.error(getFieldErrorMessage(error)!);
      }
    } else if (err instanceof Error) {
      toast.error('Unexpected error');
    }

    return Promise.reject(err);
  };
}

export const registerInterceptors = (
  apiInstances: Record<string, AxiosInstance>
) => {
  Object.values(apiInstances).forEach((instance) => {
    instance.interceptors.request.use(addAuthHeader);
    instance.interceptors.response.use(
      (response) => response,
      responseErrorHandlerBuilder(instance)
    );
  });
};
